import type { FilterJourneyIndex } from '@/src/types/srpJourneyIndex'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import useClevertapForSrp from '../../analytics/clevertap/srp'
import useActiveFilterDataIndex from '../useActiveFilterDataIndex'
import useUpdateSearchResultCache from '../updateSearchResultCache'
import useSearchResults from '../useSearchResults'

const usePriceRangeFilterManager = (filterJourneyIndex: FilterJourneyIndex = 0) => {
    const t = useTranslations('common')
    const { triggerSrpEvent } = useClevertapForSrp()

    const { filterData, isMulticity } = useSearchResults()
    const { filterDataIndex } = useActiveFilterDataIndex(filterJourneyIndex)
    const updateCache = useUpdateSearchResultCache()
    const { journeyPrice } = filterData || {}
    // Multicity: combined card price lives at journeyPrice[0] only
    const priceFilterIndex = isMulticity ? 0 : filterDataIndex
    const targetJourneyPrice = journeyPrice?.[priceFilterIndex]
    const { min: defaultMin, max: defaultMax, current: currentPriceRange } = targetJourneyPrice || {}
    const { min: currentMin, max: currentMax } = currentPriceRange || {}

    const changePriceRange = useCallback(
        (min: number, max: number) => {
            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'price',
                action_value: `${min}-${max}`
            })

            updateCache(currentData => {
                if (currentData?.filterData?.journeyPrice?.[priceFilterIndex]) {
                    currentData.filterData.journeyPrice[priceFilterIndex].current = { min, max }
                }
                return currentData as NonNullable<typeof currentData>
            })
        },
        [updateCache, priceFilterIndex, triggerSrpEvent]
    )

    const handleReset = useCallback(() => {
        const numDefaultMin = Number(defaultMin)
        const numDefaultMax = Number(defaultMax)
        if (!isNaN(numDefaultMin) && !isNaN(numDefaultMax)) {
            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'reset',
                action_value: 'price'
            })

            updateCache(currentData => {
                if (currentData?.filterData?.journeyPrice?.[priceFilterIndex]) {
                    currentData.filterData.journeyPrice[priceFilterIndex].current = {
                        min: numDefaultMin,
                        max: numDefaultMax
                    }
                }
                return currentData as NonNullable<typeof currentData>
            })
        }
    }, [updateCache, priceFilterIndex, defaultMin, defaultMax, triggerSrpEvent])

    const isPriceRangeFilterApplied = useMemo(() => {
        return currentMin !== defaultMin || currentMax !== defaultMax
    }, [currentMin, currentMax, defaultMin, defaultMax])

    return {
        currentMin,
        currentMax,
        min: defaultMin,
        max: defaultMax,
        changePriceRange,
        handleReset,
        title: t('price'),
        isPriceRangeFilterApplied
    }
}

export default usePriceRangeFilterManager
